/**
 * What the site actually looks like, and what the browser measured on it.
 *
 * This is the tab that answers the question the whole tool started from: an
 * owner sent a photo of one section of their own site that was visibly broken,
 * and nothing in the HTML said so. The screenshots are the proof an operator
 * can paste into an email; the measurements underneath are what turns "it looks
 * off" into "your heading needs 459 pixels and has 324".
 *
 * Desktop and phone side by side on purpose. Half of these sites were built
 * before anyone in the company owned a smartphone, and the difference between
 * the two frames is frequently the entire pitch.
 */
import { AlertTriangle, Monitor, Smartphone } from "lucide-react";
import type { BrainLayout, BrainProbeMeasurement, BrainShot, BrainShots, BrainViewport } from "../../../../lib/siteBrain";
import type { T } from "../../site-scanner/types";

const ICON: Record<BrainViewport, typeof Monitor> = { desktop: Monitor, mobile: Smartphone };

export function BrainLook({
  layout,
  shots,
  t,
}: {
  layout: BrainLayout | null;
  shots: BrainShots | null;
  t: T;
}) {
  const order: BrainViewport[] = ["desktop", "mobile"];
  const shotFor = (v: BrainViewport): BrainShot | undefined => shots?.shots.find((s) => s.viewport === v);
  const nothing = !shots?.shots.length && !layout?.viewports.desktop && !layout?.viewports.mobile;

  if (nothing) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">{t("brain.look.missing")}</p>
        <Failures layout={layout} t={t} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Failures layout={layout} t={t} />

      <div className="grid gap-4 lg:grid-cols-2">
        {order.map((viewport) => (
          <ViewportPanel
            key={viewport}
            viewport={viewport}
            shot={shotFor(viewport)}
            probe={layout?.viewports[viewport]}
            t={t}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Why a viewport is missing.
 *
 * Never silence. A card that shows one screenshot and says nothing about the
 * other one reads as "the phone view is fine", which is the opposite of what a
 * missing frame means.
 */
function Failures({ layout, t }: { layout: BrainLayout | null; t: T }) {
  const failures = layout?.failures ?? [];
  if (!failures.length) return null;
  return (
    <ul className="space-y-1">
      {failures.map((f) => (
        <li key={`${f.viewport}:${f.reason}`} className="flex items-start gap-2 text-xs text-muted-foreground">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          {t("brain.look.failed", { viewport: t(`brain.look.viewport.${f.viewport}`) })}{" "}
          {t(`brain.look.reason.${f.reason}`)}
        </li>
      ))}
    </ul>
  );
}

function ViewportPanel({
  viewport,
  shot,
  probe,
  t,
}: {
  viewport: BrainViewport;
  shot: BrainShot | undefined;
  probe: BrainProbeMeasurement | undefined;
  t: T;
}) {
  const Icon = ICON[viewport];
  if (!shot && !probe) return null;

  return (
    <section className="space-y-2">
      <h4 className="flex items-center gap-2 text-sm font-semibold">
        <Icon className="size-4 text-primary" />
        {t(`brain.look.viewport.${viewport}`)}
        {probe?.viewport ? (
          <span className="text-xs font-normal tabular-nums text-muted-foreground">
            {probe.viewport.width}x{probe.viewport.height}
          </span>
        ) : null}
      </h4>

      {shot?.url ? (
        // Opens full size in a tab: the frame is a tall page squeezed into a
        // card, and the detail an operator wants to show a client is not
        // readable at this size.
        <a href={shot.url} target="_blank" rel="noreferrer noopener" className="block">
          <img
            src={shot.url}
            alt={t(`brain.look.viewport.${viewport}`)}
            loading="lazy"
            className="w-full rounded-xl border border-[var(--crm-hair)] bg-[var(--crm-sunk)]"
          />
        </a>
      ) : null}

      {probe ? <Measurements probe={probe} t={t} /> : null}
    </section>
  );
}

/** The numbers behind the picture, worst first. */
function Measurements({ probe, t }: { probe: BrainProbeMeasurement; t: T }) {
  const rows: { key: string; label: string; detail?: string }[] = [];

  if (probe.scrollsSideways) {
    rows.push({
      key: "sideways",
      label: t("brain.look.sideways", {
        doc: probe.documentWidth ?? 0,
        view: probe.viewport?.width ?? 0,
      }),
    });
  }

  for (const entry of probe.overflowText ?? []) {
    const over = Number(entry.over ?? 0);
    rows.push({
      key: `overflow:${entry.at}:${over}`,
      label: t(entry.mode === "cut" ? "brain.look.textCut" : "brain.look.textSpills", {
        visible: Number(entry.visible ?? 0),
        needed: Number(entry.needed ?? 0),
      }),
      detail: String(entry.text ?? entry.at),
    });
  }

  for (const entry of (probe.tinyTargets ?? []).slice(0, 4)) {
    rows.push({
      key: `tap:${entry.at}:${String(entry.w)}x${String(entry.h)}`,
      label: t("brain.look.tapTarget", { w: Number(entry.w ?? 0), h: Number(entry.h ?? 0) }),
      detail: String(entry.text || entry.at),
    });
  }

  for (const entry of (probe.smallText ?? []).slice(0, 3)) {
    rows.push({
      key: `small:${entry.at}:${String(entry.px)}`,
      label: t("brain.look.smallText", { px: Number(entry.px ?? 0) }),
      detail: String(entry.text ?? entry.at),
    });
  }

  if (!rows.length) {
    return <p className="text-xs text-muted-foreground">{t("brain.look.clean")}</p>;
  }

  return (
    <ul className="space-y-1">
      {rows.map((row) => (
        <li key={row.key} className="rounded-xl bg-[var(--crm-sunk)] px-3 py-2 text-sm">
          {row.label}
          {row.detail ? <span className="mt-0.5 block truncate text-xs text-muted-foreground">{row.detail}</span> : null}
        </li>
      ))}
    </ul>
  );
}
